import en from "@/i18n/en/en";
import es from "@/i18n/es/es";
import { toLower } from "lodash-es";

export const localeMapping = {
  en: en,
  es: es,
};

export const fallbackLocale = "en";

// An explicit sign-in choice overrides a saved preference; otherwise use the device language.
export const resolveLocale = (deviceLocale: string, savedLocale?: string | null, signInLocale?: string | null): string => {
  const language = (signInLocale || savedLocale || deviceLocale).toLowerCase().split("-")[0];
  return language === "es" ? "es" : "en";
};

const TEMP_LOCALE_KEY = "temp_language_preference";

export const getTemporaryLocale = (): string | null => {
  try {
    return sessionStorage.getItem(TEMP_LOCALE_KEY);
  } catch (e) {
    return null;
  }
};

export const saveTemporaryLocale = (locale: string): void => {
  try {
    sessionStorage.setItem(TEMP_LOCALE_KEY, locale);
  } catch (e) {
    console.warn("Could not save temporary language preference", e);
  }
};

export const clearTemporaryLocale = (): void => {
  try {
    sessionStorage.removeItem(TEMP_LOCALE_KEY);
  } catch (e) {
    console.warn("Could not clear temporary language preference", e);
  }
};

export const getInitialLocale = (forceFallback: boolean): string => {
  if (forceFallback) return "en";
  const temporaryLocale = getTemporaryLocale();
  console.log("temporaryLocale", temporaryLocale);
  return temporaryLocale === "en" || temporaryLocale === "es" ? temporaryLocale : resolveLocale(navigator.language);
};

type Translations = Record<string, Record<string, string>>;

export const translate = (translations: Translations, locale: string, key: string, values: Record<string, string> = {}): string => {
  const keyLower = toLower(key);
  let translation = (translations[locale] || translations.en)[keyLower];
  if (translation === undefined) translation = translations.en[keyLower] || key;
  if (translation) {
    Object.entries(values).forEach(([placeholder, value]) => {
      translation = translation.replace(new RegExp(`{{\\s*${placeholder}\\s*}}`, "g"), value);
    });
  }
  return translation;
};

export const hasTranslation = (translations: Translations, locale: string, key: string): boolean => {
  const keyLower = toLower(key);
  return translations[locale]?.[keyLower] !== undefined || Boolean(translations.en[keyLower]);
};

export const createCountryNames = (locale: string): Intl.DisplayNames => {
  try {
    return new Intl.DisplayNames([locale || "en"], { type: "region" });
  } catch {
    return new Intl.DisplayNames(["en"], { type: "region" });
  }
};

export const translateCountry = (countryNames: Intl.DisplayNames, countryCode?: string): string => {
  if (!countryCode) return "-";
  try {
    return countryNames.of(countryCode.toUpperCase()) || countryCode;
  } catch {
    return countryCode;
  }
};
